import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { getCurrentMemberId, memberById, useRoster, useEnsureRoster } from "./roster";

/**
 * Agents user menu (AMB-359 / A3) — the top-left avatar in the platform LeftSidebar for
 * agent-tier users. The platform's own AvatarDropdown is hidden for agents (it links to
 * platform-only screens); this is the agent-scoped replacement. It mirrors the actual
 * Settings tabs — Profile / Team / Client Access — each deep-linking to its tab, plus
 * Sign Out (the agent logout path). Client selection lives in the breadcrumb switcher
 * (AppShellV8), not here — per D-1 the avatar is a USER menu, not a client switcher.
 *
 * The signed-in member resolves from the login email via the canonical roster (AMB-358);
 * an unrecognized account shows a neutral "not linked" chip rather than someone else.
 */

const MENU = [
  { label: "Profile", tab: "profile" },
  { label: "Team", tab: "team" },
  { label: "Client Access", tab: "clients" },
] as const;

export function UserMenu() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // Load + subscribe to THIS tenant's roster so the signed-in user resolves to their own
  // agency's member (not the Ambid fallback), and re-render once it arrives (AMB-438).
  useEnsureRoster();
  const roster = useRoster();
  const me = memberById(getCurrentMemberId(roster), roster);
  const initials = me?.initials ?? "?";
  const color = me?.color ?? "bg-gray-400";
  const name = me?.name ?? "Account not linked";
  const email = me?.email ?? "Log in with a team email";

  const go = (tab: string) => {
    setOpen(false);
    navigate(`/agents/settings?tab=${tab}`);
  };

  // Mirrors Settings' handleSignOut: clear the local session, return to /login.
  const signOut = () => {
    setOpen(false);
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
    } catch {
      /* ignore */
    }
    navigate("/login");
  };

  return (
    <div className="relative flex justify-center pt-5 pb-3">
      <button
        data-testid="agents-user-menu"
        onClick={() => setOpen(!open)}
        title={name}
        className={`flex h-8 w-8 items-center justify-center rounded-full ${color} text-[10px] font-bold text-white ring-2 ring-white shadow-sm transition-all duration-150 hover:shadow-md hover:ring-gray-100`}
      >
        {initials}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-[calc(100%+12px)] top-0 z-50 w-56 overflow-hidden rounded-xl border border-black/[0.06] bg-white shadow-xl">
            <div className="border-b border-black/[0.06] px-3 py-3">
              <p className="truncate text-[13px] font-semibold text-gray-900">{name}</p>
              <p className="truncate text-[11px] text-gray-400">{email}</p>
            </div>
            <div className="py-1">
              {MENU.map((m) => (
                <button
                  key={m.tab}
                  onClick={() => go(m.tab)}
                  className="block w-full px-3 py-2 text-left text-[13px] text-gray-700 hover:bg-gray-50"
                >
                  {m.label}
                </button>
              ))}
            </div>
            <div className="border-t border-black/[0.06] py-1">
              <a
                href="https://docs.launchos.com/docs/agents"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className="block w-full px-3 py-2 text-left text-[13px] text-gray-700 hover:bg-gray-50"
              >
                Documentation
              </a>
            </div>
            <div className="border-t border-black/[0.06] py-1">
              <button
                onClick={signOut}
                className="block w-full px-3 py-2 text-left text-[13px] text-gray-700 hover:bg-gray-50"
              >
                Sign Out
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
